import type { Product } from "../types/Product";
import type { Zone } from "../zones";

export const setIsEqual = <T>(a: Set<T>, b: Set<T>) => {
	const sortedArray1 = Array.from(a).sort();
	const sortedArray2 = Array.from(b).sort();

	if (sortedArray1.length !== sortedArray2.length) return false;

	return sortedArray1.every((val, i) => val === sortedArray2[i]);
};

const zonesAreMatchingProductFilter = (
	product: Product,
	zoneFromId: string,
	zoneToId: string,
): boolean => {
	if (!product.filters) {
		return false;
	}
	const { zoneListMustEqual, zoneListMustBeSubsetOf } = product.filters;

	if (zoneListMustEqual) {
		return setIsEqual(
			new Set(zoneListMustEqual),
			new Set([zoneFromId, zoneToId]),
		);
	}

	if (zoneListMustBeSubsetOf) {
		return (
			zoneListMustBeSubsetOf.some((x) => x === zoneFromId) &&
			zoneListMustBeSubsetOf.some((x) => x === zoneToId)
		);
	}

	return false;
};

const getMatchingProduct = (
	products: Product[],
	zoneFrom: string,
	zoneTo: string,
	zones: Zone[],
	profile: string,
): Product => {
	const filteredProducts = products.filter(
		(product) => product.profileId === profile,
	);
	const groupedFilteredProducts = {
		productsWithFilter: filteredProducts.filter((x) => x.filters),
		productsWithoutFilter: filteredProducts.filter((x) => x.filters === null),
	};
	const defaultProduct = groupedFilteredProducts.productsWithoutFilter[0];

	const zoneFromObj = zones.find((x) => x.name === zoneFrom);
	const zoneToObj = zones.find((x) => x.name === zoneTo);

	if (!zoneFromObj || !zoneToObj) {
		return defaultProduct;
	}

	if (groupedFilteredProducts.productsWithFilter.length > 0) {
		const exactMatch = groupedFilteredProducts.productsWithFilter.find((x) =>
			zonesAreMatchingProductFilter(x, zoneFromObj.id, zoneToObj.id),
		);

		if (exactMatch) {
			return exactMatch;
		}
	}

	return defaultProduct;
};

const getZoneCount = (
	zones: Zone[],
	zoneFromName: string,
	zoneToName: string,
): number | "UNKNOWN" => {
	const zoneFrom = zones.find((x) => x.name === zoneFromName);
	const zoneTo = zones.find((x) => x.name === zoneToName);

	if (!zoneFrom || !zoneTo) {
		return "UNKNOWN";
	}

	if (zoneFrom.id === zoneTo.id) {
		return 1;
	}

	if (zoneFrom.adjacentZoneIds.includes(zoneTo.id)) {
		return 2;
	}

	return 3;
};

export const getProductPrice = (
	products: Product[],
	zoneFrom: string,
	zoneTo: string,
	zones: Zone[],
	profile = "ADULT",
): number | "UNKNOWN" => {
	const zoneCount = getZoneCount(zones, zoneFrom, zoneTo);
	const product = getMatchingProduct(
		products,
		zoneFrom,
		zoneTo,
		zones,
		profile,
	);
	const priceInfo =
		product && product.prices.length > 1
			? product.prices.find((x) => x.nrOfZones === zoneCount)
			: product?.prices[0];
	const price = priceInfo ? priceInfo.priceInclVAT / 12 : undefined;

	return price || "UNKNOWN";
};
